// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { SohoButtonComponent } from 'ids-enterprise-ng';
import {
  LMCompactFormEvent,
  LMFieldBaseComponent,
  LMFieldValueChangedEvent,
  LMSubFormEvent
} from '@field/lm-field-base.component';
import { LMModel } from '@form/model/lm-model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { AppConstants, LMButtonType } from '@utils/app.constants';
import { Subscription } from 'rxjs';

export class LMButtonEvent implements LMFieldEvent {
  layoutObject: LMButtonLayout;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, layoutObject: LMButtonLayout) {
    this.eventType = 'ButtonEvent';
    this.event = event;
    this.layoutObject = layoutObject;
  }
}

enum LMButtonSubTypeDisplayEnum {
  ICON_BUTTON = 0,
  TEXT_BUTTON,
  TEXT_MAIL_BUTTON,
  ICON_MAIL_BUTTON,
  TEXT_LINK,
  MAIL_LINK,
  TAG
}

@Component({
  selector:    'lm-button',
  standalone: false,
  templateUrl: './lm-button.component.html',
  styleUrls:   [ './lm-button.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMButtonComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.field') isField = true;
  @HostBinding('class.lm-single-column-child') isChildOfSingleColumn = false;
  @HostBinding('class.lm-icon-button') isIconButton = false;

  @HostBinding('class.lm-button-right') isAlignRight = false;
  @HostBinding('class.lm-button-center') isAlignCenter = false;
  @HostBinding('class.lm-button-left') isAlignLeft = false;

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';
  @HostBinding('class.lm-is-hyperlink') isHyperLink() {
    return this?.displayAsLink;
  }

  @Input() layoutObject: LMButtonLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  public displayAsButton = true;
  public displayAsLink = false;
  public mailToHttpUrl = false;
  public text: string;
  public buttonIcon: string = null;
  public buttonType: string = null;
  public iconHref: string;
  public iconUrl: string;
  public validSohoIcon: boolean;
  public valid = false;
  public isDataViewFieldButton = false;
  public dataViewFieldButtonHasData = false;
  public buttonTooltip = '';

  public displayType: LMButtonSubTypeDisplayEnum;
  public layoutDisplayType;

  public buttonBackgroundClass: string;

  private buttonAlign: HorizontalAlignment = null;

  private unsub$: Subscription;
  private isCreateReportMode = false;

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMButtonSubTypeDisplayEnum;
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    if (this.lmColumnLayoutInfo && this.lmColumnLayoutInfo.nbrColumns === 1) {
      this.isChildOfSingleColumn = true;
    }

    if (this.layoutObject.hasOwnProperty('buttonType')) {
      this.buttonType = this.layoutObject.buttonType;

    } else if (this.layoutObject.iconUrl && !this.layoutObject.title) {
      this.buttonType = SohoButtonComponent.ICON;

    } else {
      this.buttonType = this.layoutObject.isPrimary ? SohoButtonComponent.PRIMARY : SohoButtonComponent.SECONDARY;
    }

    // Commenting out changing the button color to match the theme color
    //
    // let changeButtonColor: boolean;
    // if (this.lmFieldServiceAdapter.lmThemeService.getCurrentThemeId() === 'theme-new-light') {
    //   changeButtonColor = true;
    // } else {
    //   changeButtonColor = this.buttonType === SohoButtonComponent.PRIMARY;
    // }
    //
    //
    // if (changeButtonColor) {
    //
    //   if (this.lmFieldServiceAdapter.lmThemeService.getCurrentThemeId()?.includes('theme-new-light')) {
    //     if (this.buttonType === SohoButtonComponent.PRIMARY)
    //       this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getPrimaryButtonColorClass();
    //     if (this.buttonType === SohoButtonComponent.SECONDARY)
    //       this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getSecondaryButtonColorClass();
    //     if (this.buttonType === SohoButtonComponent.ICON) {
    //       this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getIconButtonColorClass();
    //     }
    //   }
    //
    //   this.subscriptionArray.push(this.lmFieldServiceAdapter.lmThemeService.getColorChangeSubject().subscribe((color) => {
    //     if (this.lmFieldServiceAdapter.lmThemeService.getCurrentThemeId() === 'theme-new-light') {
    //       if (this.buttonType === SohoButtonComponent.PRIMARY)
    //         this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getPrimaryButtonColorClass(color);
    //       if (this.buttonType === SohoButtonComponent.SECONDARY)
    //         this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getSecondaryButtonColorClass(color);
    //       if (this.buttonType === SohoButtonComponent.ICON) {
    //         this.buttonBackgroundClass = this.lmFieldServiceAdapter.lmThemeService.getIconButtonColorClass(color);
    //       }
    //     } else {
    //       this.buttonBackgroundClass = '';
    //     }
    //   }));
    // }

    if (this.field && this.field.fieldStyle && this.field.fieldStyle.align) {
      this.buttonAlign = this.field.fieldStyle.align;
      switch (this.buttonAlign) {
        case 'right' :
          this.isAlignRight = true;
          break;
        case 'center':
          this.isAlignCenter = true;
          break;
        case 'left':
          this.isAlignLeft = true;
          break;
      }
    }

    this.buttonIcon = this.layoutObject.icon;

    if (this.buttonIcon === 'internalCorrespondenceIconBlack') {
      this.buttonIcon = 'mail';
    }

    this.iconHref = '';

    if (this.layoutObject.httpUrl && this.layoutObject.httpUrl.resourceValue.indexOf('mailto:') === 0) {
      this.mailToHttpUrl = true;
    }

    if (this.layoutObject.iconUrl) {
      this.iconUrl = 'url(' + this.layoutObject.iconUrl + ')';
    }

    if (this.layoutObject.icon) {
      this.validSohoIcon = this.lmFieldServiceAdapter.lmThemeService.isValidSohoIcon(this.layoutObject.icon);
    }

    if (this.layoutObject.displayAsLink) {
      this.displayAsLink = true;
      this.displayAsButton = false;
    }

    if (this.layoutObject.displayAsTag) {
      this.displayAsLink = false;
      this.displayAsButton = false;
    }

    if (this.displayAsButton && !this.mailToHttpUrl) {
      if (this.iconUrl) {
        this.displayType = this.layoutDisplayType.ICON_BUTTON;
        this.isIconButton = !this.layoutObject.hasOwnProperty('title');
      } else {
        this.displayType = this.layoutDisplayType.TEXT_BUTTON;

      }
    } else if (this.displayAsButton && this.mailToHttpUrl) {
      if (this.iconUrl) {
        this.displayType = this.layoutDisplayType.ICON_MAIL_BUTTON;
        this.isIconButton = !this.layoutObject.hasOwnProperty('title');
      } else {
        this.displayType = this.layoutDisplayType.TEXT_MAIL_BUTTON;

      }
    } else if (this.displayAsLink && !this.mailToHttpUrl) {
      this.displayType = this.layoutDisplayType.TEXT_LINK;

    } else if (this.displayAsLink && this.mailToHttpUrl) {
      this.displayType = this.layoutDisplayType.MAIL_LINK;
    } else if (this.layoutObject.displayAsTag) {
      this.displayType = this.layoutDisplayType.TAG;
    }

    this.valid = this.checkValidity();

    this.setDataViewFieldButtonState();

    this.updateButtonTooltip();

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe(() => {
        // triggerValue changes to indicate the formatMessageRule pipe to fire
        // triggerValue is set in lm-field-base.component; do not set it here

        this.valid = this.checkValidity();

        this.setDataViewFieldButtonState();

        this.updateButtonTooltip();
      })
    );

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-button`;

    this.unsub$ = this.lmFieldServiceAdapter.lmFormService.isCreateReportMode$.subscribe(state => (this.isCreateReportMode = state, this.valid = this.checkValidity(), this.updateButtonTooltip()));
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsub$.unsubscribe();
  }

  // duplicate code in lm-hyperlink
  // FYI.  Rich client does NOT check validity of a navigation button on a form
  checkValidity() {
    if (this.model?.isDrillAroundMode()) {
      return false;
    }
    
    // Valid types if isDataModificationNotAllowed = true: 
    // 1. NAVIGATION 2. LIST_REFERENCE 3. SUBFORM 4. EXPORTDOCUMENT 5. VIEWDOCUMENT 6. HTTPURL 7. SHOWEDITOR
    const isButtonValidInArchiveMode = [ LMButtonType.ACTION, LMButtonType.RELATED, LMButtonType.FORMINVOKE ]
      .find( buttonType => buttonType.toString() === this.layoutObject?.type );
    if (this.lmFieldServiceAdapter.lmContextService.isDataModificationNotAllowed() && isButtonValidInArchiveMode) {
      return false;
    }

    if (this.isCreateReportMode) {
      return false;
    }

    let valid = true;
    // if (this.field.type === AppConstants.BUTTON_TYPE_NAVIGATION) {
    //   // TODO: LMCLIENT-21757, LMCLIENT-23380, LMCLIENT-25651; temp work around until LMRK-83694 is completed
    //   if (this.lmFieldServiceAdapter.lmFormService.isStepProcessForm(this.model.formSpec)) {
    //     return true;
    //   }
    //
    //   valid = !this.lmFieldServiceAdapter.lmConditionUtilService.isNavigationInvalid(this.layoutObject, this.model);
    // }

    if (this.layoutObject.validity) {
      valid = this.lmFieldServiceAdapter.lmConditionUtilService.isValid(this.layoutObject.validity as LMCondition[], this.model);
    }

    if (this.layoutObject.isStandardAction) {
      if (this.layoutObject.standardActionName !== AppConstants.STANDARD_SAVE) {
        // only save supported, disable all other standard actions
        valid = false;
      }
    }

    return valid;
  }

  setDataViewFieldButtonState() {
    if (this.layoutObject.hasOwnProperty('dataViewFieldButton')) {
      this.isDataViewFieldButton = true;
      this.dataViewFieldButtonHasData = (!!this.modelField.value && this.modelField.value.trim() !== '');
    }
  }

  updateButtonTooltip() {
    if (this.layoutObject.openInOverlay && this.valid) {
      this.buttonTooltip = Soho.Locale.translate('FlyoutOpenFullScreen');
    } else if (this.labelResourceValue) {
      this.buttonTooltip = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
    } else {
      this.buttonTooltip = '';
    }
  }

  mailTo() {
    const messageRule = this.layoutObject.httpUrl;
    window.location.href = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(messageRule, this.model, this.specFields);
  }

  onClickEvent(event: Event) {
    if (this.mailToHttpUrl) {
      this.mailTo();

      // TODO: need to evaluate this again; re: LMCLIENT-26683:
    } else if (this.compactFormDialogSpec) {
      this.fieldEvent.emit(new LMCompactFormEvent(event, this.layoutObject.fieldId, this.compactFormDialogSpec.label,
        this.compactFormDialogSpec.layout, this.layoutObject.moreSpecFields));

    } else if (this.layoutObject.type === LMButtonType.SHOWEDITOR && this.modelField.isCompoundField) {
      const title = this.specFields[ this.layoutObject.fieldId ].label;
      const layout: LMFormLayout = { sections: this.layoutObject.layout as Array<LMFormLayoutSection> };

      this.fieldEvent.emit(new LMCompactFormEvent(event, this.field.fieldId, title, layout, this.layoutObject.moreSpecFields));

    } else if (this.isSubForm) {
      this.fieldEvent.emit(new LMSubFormEvent(event, this.layoutObject.fieldId, this.field.buttonSubForm.title,
        this.field.buttonSubForm));
    } else if (this.layoutObject.fireInitialValue) {
      this.fireInitialValueRule();
    } else {
      this.fieldEvent.emit(new LMButtonEvent(event, this.layoutObject));
    }
  }

  fireInitialValueRule() {
    if (this.layoutObject.fireInitialValue) {
      this.fireWhenValueChangedEvent(this.bindId);
    }
  }

  private fireWhenValueChangedEvent(bindId: string) {
    this.fieldEvent.emit(
      new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'FireInitialValueRuleEvent', bindId));
  }
}
